import {
    addDocument,
    getCollectionDocuments,
    queryDocuments,
    setDocument,
    updateDocument,
    getDocumentById
} from "../firebase/firestore";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase/config";

export const learningService = {
    async createLearningPath(data) {
        return await addDocument("learningPaths", {
            name: data.name,
            description: data.description,
            difficulty: data.difficulty,
            creditsOnCompletion: data.creditsOnCompletion || 100,
            icon: data.icon || "book",
            color: data.color || "#667eea",
            duration: data.duration || "4 weeks",
            levels: data.levels || [],
            active: true,
            createdAt: new Date(),
            updatedAt: new Date()
        });
    },

    async getLearningPaths(active = true) {
        if (active) {
            return await queryDocuments("learningPaths", [["active", "==", true]]);
        }
        return await getCollectionDocuments("learningPaths");
    },

    async getLearningPathById(pathId) {
        return await getDocumentById("learningPaths", pathId);
    },

    async updateLearningPath(pathId, data, actor = null) {
        if (!data?.name || !String(data.name).trim()) {
            throw new Error("Learning path title is required");
        }
        if (!data?.description || !String(data.description).trim()) {
            throw new Error("Learning path description is required");
        }
        if (!Array.isArray(data.levels) || data.levels.length === 0) {
            throw new Error("At least one module/level is required");
        }
        for (const level of data.levels) {
            if (!level?.name || !String(level.name).trim()) {
                throw new Error("Each module must have a title");
            }
            if (!Array.isArray(level.questions) || level.questions.length === 0) {
                throw new Error(`Module "${level.name}" must include at least one assessment question`);
            }
            if (Number(level.qualifyingScore || 0) < 1 || Number(level.qualifyingScore || 0) > 100) {
                throw new Error(`Module "${level.name}" scoring criteria must be between 1 and 100`);
            }
        }
        await updateDocument("learningPaths", pathId, {
            ...data,
            updatedAt: new Date()
        });
        if (actor?.uid) {
            await this.logLearningPathAudit({
                pathId,
                action: "learning_path_updated",
                actorId: actor.uid,
                actorEmail: actor.email || "",
                payload: {
                    name: data.name,
                    difficulty: data.difficulty,
                    levelsCount: data.levels.length
                }
            });
        }
    },

    async createLevel(pathId, data) {
        const path = await this.getLearningPathById(pathId);
        const levels = path?.levels || [];
        const newLevel = {
            id: `level_${Date.now()}`,
            pathId,
            name: data.name,
            description: data.description,
            order: data.order || levels.length + 1,
            testId: data.testId,
            questions: data.questions || [],
            qualifyingScore: data.qualifyingScore || 70,
            duration: data.duration || "30 mins",
            active: true,
            createdAt: new Date()
        };

        levels.push(newLevel);
        await updateDocument("learningPaths", pathId, { levels });
        return newLevel.id;
    },

    async updateLevel(pathId, levelId, data) {
        const path = await this.getLearningPathById(pathId);
        const levels = path?.levels || [];
        const levelIndex = levels.findIndex((l) => l.id === levelId);

        if (levelIndex !== -1) {
            levels[levelIndex] = {
                ...levels[levelIndex],
                ...data,
                updatedAt: new Date()
            };
            await updateDocument("learningPaths", pathId, { levels });
        }
    },

    async deleteLevel(pathId, levelId) {
        const path = await this.getLearningPathById(pathId);
        const levels = (path?.levels || []).filter((l) => l.id !== levelId);
        await updateDocument("learningPaths", pathId, { levels });
    },

    async getLevelsByPath(pathId) {
        const path = await this.getLearningPathById(pathId);
        return (path?.levels || []).sort((a, b) => a.order - b.order);
    },

    async getLevelById(pathId, levelId) {
        const path = await this.getLearningPathById(pathId);
        return (path?.levels || []).find((l) => l.id === levelId);
    },

    async createStudentProgress(userId, pathId) {
        const progressId = `${userId}_${pathId}`;
        return await setDocument("studentProgress", progressId, {
            userId,
            pathId,
            currentLevel: 0,
            levelStatus: {},
            totalCreditsEarned: 0,
            pathCompleted: false,
            completedAt: null,
            createdAt: new Date(),
            updatedAt: new Date()
        });
    },

    async getStudentProgress(userId, pathId) {
        const progressId = `${userId}_${pathId}`;
        try {
            return await getDocumentById("studentProgress", progressId);
        } catch (error) {
            if (error?.code === "permission-denied" || error?.code === "permission_denied") {
                return null;
            }
            throw error;
        }
    },

    async updateLevelAttempt(userId, pathId, levelId, score, qualifyingScore) {
        const progressId = `${userId}_${pathId}`;
        let progress = await this.getStudentProgress(userId, pathId);

        if (!progress) {
            await this.createStudentProgress(userId, pathId);
            progress = {
                id: progressId,
                userId,
                pathId,
                currentLevel: 0,
                levelStatus: {},
                totalCreditsEarned: 0,
                pathCompleted: false,
                completedAt: null
            };
        }

        const levelStatus = progress?.levelStatus || {};
        const currentStatus = levelStatus[levelId] || {
            status: "locked",
            attempts: 0,
            lastAttemptDate: null,
            highestScore: 0,
            cleared: false
        };

        const isPassed = score >= qualifyingScore;
        const newStatus = {
            ...currentStatus,
            attempts: currentStatus.attempts + 1,
            lastAttemptDate: new Date(),
            highestScore: Math.max(currentStatus.highestScore, score),
            cleared: isPassed,
            status: isPassed ? "cleared" : "active"
        };

        levelStatus[levelId] = newStatus;

        const path = await this.getLearningPathById(pathId);
        const levels = path?.levels || [];
        const clearedLevels = Object.values(levelStatus).filter((s) => s.cleared).length;
        const isPathCompleted = clearedLevels === levels.length;

        let creditsEarned = 0;
        if (isPassed && !currentStatus.cleared) {
            creditsEarned = path?.creditsOnCompletion || 100;
        }

        await setDocument("studentProgress", progressId, {
            levelStatus,
            currentLevel: clearedLevels,
            totalCreditsEarned: (progress?.totalCreditsEarned || 0) + creditsEarned,
            pathCompleted: isPathCompleted,
            completedAt: isPathCompleted ? new Date() : null
        }, true);

        // Award credits to user when path completed for the first time
        if (isPathCompleted && creditsEarned > 0) {
            try {
                const { default: creditService } = await import("./creditService");
                await creditService.awardAutoCredits(userId, "path_completed", pathId, creditsEarned);
            } catch (e) {
                // best-effort credit award; do not block learning flow
                console.error("Failed to award credits for path:", e);
            }
        }

        return {
            passed: isPassed,
            creditsEarned,
            status: newStatus,
            pathCompleted: isPathCompleted
        };
    },

    async canAttemptLevel(userId, pathId, levelId) {
        const progress = await this.getStudentProgress(userId, pathId);
        const levelStatus = progress?.levelStatus?.[levelId];

        if (!levelStatus) {
            return { canAttempt: true, reason: null };
        }

        if (levelStatus.cleared) {
            return { canAttempt: true, reason: null, reviewMode: true };
        }

        if (levelStatus.attempts > 0 && levelStatus.lastAttemptDate) {
            const lastAttempt = new Date(levelStatus.lastAttemptDate);
            const now = new Date();
            const daysSinceLastAttempt = (now - lastAttempt) / (1000 * 60 * 60 * 24);

            if (daysSinceLastAttempt < 7) {
                const daysRemaining = Math.ceil(7 - daysSinceLastAttempt);
                return {
                    canAttempt: false,
                    reason: `Please wait ${daysRemaining} days before retrying`
                };
            }
        }

        return { canAttempt: true, reason: null, reviewMode: false };
    },

    async getStudentProgressSummary(userId) {
        const progresses = await queryDocuments("studentProgress", [["userId", "==", userId]]);
        return progresses;
    },

    async getPathProgressList(pathId) {
        const progresses = await queryDocuments("studentProgress", [["pathId", "==", pathId]]);

        const enrichedProgresses = await Promise.all(
            progresses.map(async (p) => {
                const user = await getDocumentById("users", p.userId);
                return {
                    ...p,
                    userName: `${user?.firstName} ${user?.lastName}`,
                    userEmail: user?.email
                };
            })
        );

        return enrichedProgresses;
    },

    async getPathStats(pathId) {
        const progresses = await queryDocuments("studentProgress", [["pathId", "==", pathId]]);
        const completedCount = progresses.filter((p) => p.pathCompleted).length;
        const totalEnrolled = progresses.length;
        const avgCredits = progresses.length > 0
            ? progresses.reduce((sum, p) => sum + (p.totalCreditsEarned || 0), 0) / progresses.length
            : 0;

        return {
            totalEnrolled,
            completedCount,
            completionRate: totalEnrolled > 0 ? (completedCount / totalEnrolled) * 100 : 0,
            avgCreditsEarned: avgCredits
        };
    },

    buildScoreRow(progress, user, path) {
        const levelStatus = progress?.levelStatus || {};
        const statuses = Object.values(levelStatus);
        const totalLevels = Math.max(1, path?.levels?.length || 1);
        const clearedLevels = statuses.filter((s) => !!s.cleared).length;
        const progressPercent = Math.round((clearedLevels / totalLevels) * 100);
        const attemptedLevels = statuses.filter((s) => (s.attempts || 0) > 0).length;
        const highestScores = statuses
            .map((s) => Number(s.highestScore || 0))
            .filter((v) => Number.isFinite(v) && v > 0);
        const performanceScore = highestScores.length
            ? Math.round(highestScores.reduce((sum, val) => sum + val, 0) / highestScores.length)
            : 0;
        const started = attemptedLevels > 0 || progressPercent > 0;
        const completionStatus = progress?.pathCompleted ? "completed" : (started ? "in_progress" : "not_started");
        return {
            id: `${path?.id}_${progress?.userId}`,
            pathId: path?.id || "",
            pathName: path?.name || "",
            userId: progress?.userId || "",
            userName: user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.displayName || "Unknown" : "Unknown",
            userEmail: user?.email || "N/A",
            started,
            completionStatus,
            progressPercent,
            clearedLevels,
            totalLevels: path?.levels?.length || 0,
            performanceScore,
            attempts: statuses.reduce((sum, s) => sum + Number(s.attempts || 0), 0),
            totalCreditsEarned: Number(progress?.totalCreditsEarned || 0),
            pathCompleted: !!progress?.pathCompleted,
            updatedAt: progress?.updatedAt || progress?.createdAt || null
        };
    },

    async getComprehensiveScoreboard(pathId = null) {
        const paths = pathId
            ? [await this.getLearningPathById(pathId)].filter(Boolean)
            : await this.getLearningPaths(false);
        const rows = [];
        for (const path of paths) {
            const progresses = await queryDocuments("studentProgress", [["pathId", "==", path.id]]);
            for (const progress of progresses) {
                const user = await getDocumentById("users", progress.userId);
                rows.push(this.buildScoreRow(progress, user, path));
            }
        }
        return rows;
    },

    listenComprehensiveScoreboard(pathId, cb) {
        const constraints = pathId ? [where("pathId", "==", pathId)] : [];
        const q = query(collection(db, "studentProgress"), ...constraints);
        const unsub = onSnapshot(q, async (snap) => {
            const progresses = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            const pathMap = new Map();
            const rows = [];
            for (const progress of progresses) {
                let path = pathMap.get(progress.pathId);
                if (!path) {
                    path = await this.getLearningPathById(progress.pathId);
                    pathMap.set(progress.pathId, path);
                }
                const user = await getDocumentById("users", progress.userId);
                rows.push(this.buildScoreRow(progress, user, path || {}));
            }
            cb(rows);
        });
        return unsub;
    },

    async logLearningPathAudit({ pathId, action, actorId, actorEmail, payload = {} }) {
        try {
            await addDocument("audit_logs", {
                module: "learning_paths",
                pathId,
                action,
                actorId,
                actorEmail,
                payload,
                createdAt: new Date()
            });
        } catch (_) {
        }
    }
};

export default learningService;
